import { createMemo } from "solid-js";
import {
  CheckoutStep,
  useCheckoutFlow,
} from "@/contexts/checkout-flow/checkout-flow.provider";
import { submitCampaign } from "@/services/checkout.service";
import { convertSelectionsToCampaigns } from "@/utils/checkout.utils";
import {
  publishCampaignSelectionBackClick,
  publishCampaignSelectionContinueClick,
  publishFinalReviewSubmitClick,
  publishCancelCampaignBackClick,
} from "@/services/analytics.service";

export interface StepConfig {
  backAction: () => void;
  primaryAction: () => void;
  primaryText: string;
  backText: string;
  showPrimaryButton?: boolean;
  disablePrimaryButton?: boolean;
}

export const useCheckoutFlowNavigation = () => {
  const {
    currentStep,
    setCurrentStep,
    selections,
    setShowOrderConfirmation,
    isSubmitting,
    setIsSubmitting,
    dealerId,
    exitFeaturedDealer,
    setShowGlobalError,
  } = useCheckoutFlow();

  const handleSubmitReservation = async () => {
    setIsSubmitting(true);
    try {
      const campaigns = convertSelectionsToCampaigns(selections());
      await submitCampaign(dealerId(), { campaigns });
      setShowOrderConfirmation(true);
    } catch {
      setShowGlobalError(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const stepConfig: Record<CheckoutStep, StepConfig> = {
    [CheckoutStep.CAMPAIGN_LENGTH]: {
      backAction: () => {
        publishCampaignSelectionBackClick();
        exitFeaturedDealer();
      },
      primaryAction: () => {
        publishCampaignSelectionContinueClick();
        setCurrentStep(CheckoutStep.REVIEW);
      },
      primaryText: "Continue",
      backText: "Back",
      showPrimaryButton: true,
    },
    [CheckoutStep.REVIEW]: {
      backAction: () => setCurrentStep(CheckoutStep.CAMPAIGN_LENGTH),
      primaryAction: async () => {
        publishFinalReviewSubmitClick();
        await handleSubmitReservation();
      },
      primaryText: "Submit reservation",
      backText: "Back",
      showPrimaryButton: true,
    },
    [CheckoutStep.CANCEL]: {
      backAction: () => {
        publishCancelCampaignBackClick();
        exitFeaturedDealer();
      },
      primaryAction: () => {}, // Not used at cancel step
      primaryText: "Confirm",
      backText: "Back to home page",
      showPrimaryButton: false,
    },
  };

  const config = () => stepConfig[currentStep()];

  const primaryButtonText = createMemo(() => {
    const step = currentStep();
    if (step === CheckoutStep.REVIEW && isSubmitting()) {
      return "Submitting...";
    }
    return config().primaryText;
  });

  const disablePrimaryButton = createMemo(() => {
    if (currentStep() === CheckoutStep.CAMPAIGN_LENGTH) {
      return selections().length === 0;
    }
    if (currentStep() === CheckoutStep.REVIEW) {
      return isSubmitting();
    }
    return false;
  });

  return {
    config,
    stepConfig,
    exitFeaturedDealer,
    disablePrimaryButton,
    primaryButtonText,
  };
};
